import {
  Alert,
  AlertIcon,
  FormControl,
  FormHelperText,
  FormLabel,
  Radio,
  RadioGroup,
  Stack,
} from "@chakra-ui/react";
import { useTranslation } from "react-i18next";
import {
  InjunctionAnswer,
  type InjunctionSuspension,
  InjunctionSuspensionPeriod,
  InjunctionSuspensionRule,
} from "../../../autogen/bff-api";

interface Props {
  suspension: InjunctionSuspension;
  answer: InjunctionAnswer | null;
  onChange: (answer: InjunctionAnswer) => void;
}

export const InjunctionQuestion = ({ suspension, answer, onChange }: Props) => {
  const { t } = useTranslation();
  const citation = injunctionCitation(suspension);
  const copy =
    suspension.period === InjunctionSuspensionPeriod.IntentionNotice
      ? {
          question: t(
            "Did any supplier request an interim injunction within ten days of the day after the intention notice was published?",
          ),
          none: t("No, no supplier requested an interim injunction within those ten days"),
          help: t(
            "A request made within ten days of the day after the intention notice was published suspends the right to conclude the contract until the court has decided ({{citation}}).",
            { citation },
          ),
        }
      : {
          question: t("Did any supplier request an interim injunction before the standstill period ended?"),
          none: t("No, no supplier requested an interim injunction before the standstill period ended"),
          help: t(
            "A request made during the standstill period suspends the right to conclude the contract until the court has decided ({{citation}}).",
            { citation },
          ),
        };
  return (
    <FormControl isRequired>
      <FormLabel>{copy.question}</FormLabel>
      <RadioGroup value={answer ?? ""} onChange={(value) => onChange(value as InjunctionAnswer)}>
        <Stack spacing="2">
          <Radio value={InjunctionAnswer.NotRefused}>
            {t("Yes, and the court has not refused the request")}
          </Radio>
          <Radio value={InjunctionAnswer.RefusedOrWithdrawn}>
            {t("Yes, but the court refused the request, or it was withdrawn")}
          </Radio>
          <Radio value={InjunctionAnswer.NoneWithinStandstill}>{copy.none}</Radio>
        </Stack>
      </RadioGroup>
      <FormHelperText>{copy.help}</FormHelperText>
      {answer === InjunctionAnswer.NotRefused && (
        <Alert status="warning" rounded="md" fontSize="sm" mt="2" alignItems="flex-start">
          <AlertIcon />
          {t(
            "The contract may not be concluded while the request is pending or the injunction stands ({{citation}}). Once the court has refused the request, or it is withdrawn, record the date and choose that answer.",
            { citation },
          )}
        </Alert>
      )}
    </FormControl>
  );
};

export const injunctionCitation = ({ rule, period }: InjunctionSuspension): string => {
  const paragraph = period === InjunctionSuspensionPeriod.IntentionNotice ? " (1) b" : "";
  switch (rule) {
    case InjunctionSuspensionRule.Foa253:
      return `FOA § 25-3${paragraph}`;
    case InjunctionSuspensionRule.Foa103:
      return "FOA § 10-3";
    case InjunctionSuspensionRule.Foa3010:
      return `FOA § 30-10${paragraph}`;
    case InjunctionSuspensionRule.Forsyning213:
      return `forsyningsforskriften § 21-3${paragraph}`;
    case InjunctionSuspensionRule.Forsyning269:
      return `forsyningsforskriften § 26-9${paragraph}`;
  }
};

export const injunctionAnswerMissing = (asked: boolean, answer: InjunctionAnswer | null): boolean =>
  asked && (answer == null || answer === InjunctionAnswer.NotRefused);

export const injunctionField = (
  asked: boolean,
  answer: InjunctionAnswer | null,
): { injunction?: InjunctionAnswer } => (asked && answer != null ? { injunction: answer } : {});
